import React, { useEffect, useState } from "react";
import { notification, Modal, Tooltip } from "antd";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { cn } from "../../lib/utils";
import { TestRunService } from "../../services/TestRunService";
import { usePermissions } from "../../PermissionsProvider";
import moment from "moment";
import {
  TestSet,
  TestRun,
  TestRunRequest,
  TestRunQualitativeEvaluationSummary,
  IntentGroup,
} from "../../model_gen/evaluation";
import ConfigurationPanel from "../configuration/ConfigurationPanel";
import { TestSetService } from "../../services/TestSetService";
import { IntentService } from "../../services/IntentService";
import TestRunRequestComparison from "./TestRunRequestComparison";
import RequestRepresentationModal from "../Request/RequestRepresentationModal";
import TestRunRequestsTable from "./TestRunRequestsTable";

const TestRunDetail: React.FC = () => {
  const { intentGroupId, testSetId, testRunId } = useParams();
  const { apiKey } = usePermissions();
  const navigate = useNavigate();
  const [intentGroup, setIntentGroup] = useState<IntentGroup | null>(null);
  const [testSet, setTestSet] = useState<TestSet | null>(null);
  const [testRun, setTestRun] = useState<TestRun | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isConfigModalVisible, setIsConfigModalVisible] = useState(false);
  const [requests, setRequests] = useState<TestRunRequest[]>([]);
  const [isRequestsLoading, setIsRequestsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [requestsByPage, setRequestsByPage] = useState<
    Record<number, TestRunRequest[]>
  >({});
  const [totalRequests, setTotalRequests] = useState<number>(0);
  const [testRunResults, setTestRunResults] =
    useState<TestRunQualitativeEvaluationSummary | null>(null);
  const [isResultsLoading, setIsResultsLoading] = useState<boolean>(false);
  const [selectedScores, setSelectedScores] = useState<number[]>([]);
  const [isComparisonModalVisible, setIsComparisonModalVisible] =
    useState(false);
  const [selectedComparisonRequestId, setSelectedComparisonRequestId] =
    useState<number | null>(null);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(
    null
  );
  const [isRepresentationModalVisible, setIsRepresentationModalVisible] =
    useState(false);

  useEffect(() => {
    if (intentGroupId) {
      IntentService.getIntentGroup(
        Number(intentGroupId),
        false,
        setIsLoading,
        apiKey
      ).subscribe(
        (intentGroup: IntentGroup) => {
          setIntentGroup(intentGroup);
        },
        (error: any) => {
          console.error(error);
        }
      );
    }
  }, [intentGroupId]);

  useEffect(() => {
    if (testSetId) {
      TestSetService.getTestSet(testSetId, null, apiKey).subscribe(
        (data: TestSet) => {
          setTestSet(data);
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: `Failed to fetch test set: ${error}`,
          });
        }
      );
    }
  }, [testSetId]);

  useEffect(() => {
    if (testRunId) {
      setIsLoading(true);
      TestRunService.getTestRun(
        Number(testRunId),
        setIsLoading,
        apiKey
      ).subscribe(
        (data: TestRun) => {
          setTestRun(data);
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: `Failed to fetch test run: ${error}`,
          });
          setIsLoading(false);
        }
      );
    }
  }, [testRunId]);

  useEffect(() => {
    if (testRunId && !requestsByPage[currentPage]) {
      fetchRequests(currentPage);
    } else {
      setRequests(requestsByPage[currentPage]);
    }
  }, [testRunId, currentPage, requestsByPage, selectedScores]);

  useEffect(() => {
    if (testRunId) {
      setIsResultsLoading(true);
      TestRunService.getTestRunResults(
        Number(testRunId),
        setIsResultsLoading,
        apiKey
      ).subscribe(
        (data: any) => {
          setTestRunResults(data);
          setIsResultsLoading(false);
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: `Failed to fetch test run results: ${error}`,
          });
          setIsResultsLoading(false);
        }
      );
    }
  }, [testRunId]);

  const fetchRequests = (page: number) => {
    const offset = (page - 1) * 10;
    setIsRequestsLoading(true);
    TestRunService.getTestRunRequests(
      Number(testRunId),
      offset,
      10,
      setIsRequestsLoading,
      apiKey,
      selectedScores.length > 0 ? selectedScores : null
    ).subscribe(
      (response: { items: TestRunRequest[]; total: number }) => {
        setRequests(response.items);
        setTotalRequests(response.total);
        setRequestsByPage((prev) => ({
          ...prev,
          [page]: response.items,
        }));
        setIsRequestsLoading(false);
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: `Failed to fetch requests: ${error}`,
        });
        setIsRequestsLoading(false);
      }
    );
  };

  const handleRowClick = (record: TestRunRequest) => {
    console.log("Clicked TestRunRequest", record.id);
    // const sessionId = record.original_request?.session_id;
    // const requestId = record.original_request?.request_id;

    // if (applicationId && sessionId && requestId) {
    //   navigate(`/application/${applicationId}/session/${encodeURIComponent(sessionId)}/request/${requestId}`);
    // }
  };

  const handleScoreClick = (score: number) => {
    setSelectedScores((prevScores) => {
      const newScores = prevScores.includes(score)
        ? prevScores.filter((s) => s !== score)
        : [...prevScores, score];

      setRequestsByPage({});
      return newScores;
    });
  };

  // const handleOpenRepresentationModal = (record: TestRunRequest) => {
  //   setSelectedRequestId(record.original_request_id);
  //   setIsRepresentationModalVisible(true);
  // };

  const handleCompareClick = (requestId: number) => {
    setSelectedComparisonRequestId(requestId);
    setIsComparisonModalVisible(true);
  };

  const handleScoreFilterClear = () => {
    setSelectedScores([]);
    setRequestsByPage({});
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/intent-groups")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Intent Groups
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                {intentGroup && (
                  <>
                    <li>
                      <button 
                        onClick={() => navigate(`/intent-group/${intentGroup.id}`)}
                        className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                      >
                        {intentGroup.intent_name}
                      </button>
                    </li>
                    <li className="text-muted-foreground/40">
                      <span>/</span>
                    </li>
                    <li>
                      <button 
                        onClick={() => navigate(`/intent-group/${intentGroup.id}?tab=testsets`)}
                        className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                      >
                        Test Sets
                      </button>
                    </li>
                    <li className="text-muted-foreground/40">
                      <span>/</span>
                    </li>
                  </>
                )}
                {testSet && (
                  <>
                    <li>
                      <button 
                        onClick={() => navigate(`/intent-group/${intentGroup?.id}/testsets/${testSetId}`)}
                        className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                      >
                        {testSet.name}
                      </button>
                    </li>
                    <li className="text-muted-foreground/40">
                      <span>/</span>
                    </li>
                  </>
                )}
                <li>
                  <span className="text-maitai-green font-gc-med">
                    {testRun ? `Test Run ${testRun.id}` : "Test Run"}
                  </span>
                </li>
              </ol>
            </nav>

            {/* Page header */}
            <div className="space-y-1">
              {isLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-10 w-64" />
                  <Skeleton className="h-5 w-96" />
                </div>
              ) : (
                <>
                  <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                    {testRun ? `Test Run ${testRun.id}` : "Test Run"}
                  </h1>
                  <p className="text-lg text-muted-foreground font-gc-reg">
                    {testSet?.name} test run results and analysis
                  </p>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Details Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            <h2 className="text-xl font-gc-med text-foreground mb-6">Details</h2>
            {isLoading ? (
              <div className="grid md:grid-cols-3 gap-6">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-5 w-32" />
                  </div>
                ))}
              </div>
            ) : testRun ? (
              <div className="grid md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <div className="text-sm text-muted-foreground font-gc-reg">Date Created</div>
                  <div className="text-sm font-gc-reg text-foreground">
                    {moment(testRun.date_created).format("MMM D, YYYY HH:mm:ss")}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="text-sm text-muted-foreground font-gc-reg">Date Completed</div>
                  <div className="text-sm font-gc-reg text-foreground">
                    {testRun.date_completed
                      ? moment(testRun.date_completed).format("MMM D, YYYY HH:mm:ss")
                      : "—"}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="text-sm text-muted-foreground font-gc-reg">Status</div>
                  <div>
                    <span className={cn(
                      "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium",
                      testRun.status === "COMPLETED" && "bg-green-100 text-green-800 border-green-200 dark:bg-green-900 dark:text-green-200",
                      testRun.status === "ERROR" && "bg-red-100 text-red-800 border-red-200 dark:bg-red-900 dark:text-red-200",
                      testRun.status === "PENDING" && "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900 dark:text-yellow-200",
                      !testRun.status && "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900 dark:text-gray-200"
                    )}>
                      {testRun.status}
                    </span>
                  </div>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <div className="text-sm text-muted-foreground font-gc-reg">Description</div>
                  <div className="text-sm font-gc-reg text-foreground">
                    {testRun.description || "No description provided"}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="text-sm text-muted-foreground font-gc-reg">Configuration</div>
                  <div>
                    <Button
                      variant="ghost"
                      className="p-0 h-auto text-sm font-gc-reg text-foreground hover:text-maitai-green transition-colors underline"
                      onClick={() => setIsConfigModalVisible(true)}
                    >
                      {testRun.config?.model || "No model specified"}
                    </Button>
                  </div>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>

        {/* Results Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            <h2 className="text-xl font-gc-med text-foreground mb-6">Results</h2>
            {isResultsLoading ? (
              <div className="grid md:grid-cols-4 gap-6 mb-8">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-5 w-16" />
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className="grid md:grid-cols-4 gap-6 mb-8">
                  <div className="space-y-2">
                    <div className="text-sm text-muted-foreground font-gc-reg"># Requests</div>
                    <div className="text-sm font-gc-reg text-foreground">
                      {testRunResults?.total_requests || 0}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm text-muted-foreground font-gc-reg">Completed %</div>
                    <div className="text-sm font-gc-reg text-foreground">
                      {testRunResults
                        ? `${Math.round(
                            (testRunResults.completed_requests /
                              testRunResults.total_requests) *
                              100
                          )}%`
                        : "—"}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm text-muted-foreground font-gc-reg">Error %</div>
                    <div className="text-sm font-gc-reg text-foreground">
                      {testRunResults
                        ? `${Math.round(
                            (testRunResults.error_requests /
                              testRunResults.total_requests) *
                              100
                          )}%`
                        : "—"}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm text-muted-foreground font-gc-reg">Response Time</div>
                    <div className="text-sm font-gc-reg text-foreground">
                      {testRunResults?.response_time_percentiles?.p50 ? (
                        <Tooltip
                          title={
                            <div>
                              <div>
                                P50:{" "}
                                {Math.round(
                                  testRunResults.response_time_percentiles.p50
                                )}
                                ms
                              </div>
                              <div>
                                P90:{" "}
                                {Math.round(
                                  testRunResults.response_time_percentiles.p90
                                )}
                                ms
                              </div>
                              <div>
                                P95:{" "}
                                {Math.round(
                                  testRunResults.response_time_percentiles.p95
                                )}
                                ms
                              </div>
                            </div>
                          }>
                          <span>
                            {Math.round(
                              testRunResults.response_time_percentiles.p50
                            )}
                            ms
                          </span>
                        </Tooltip>
                      ) : (
                        "—"
                      )}
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="text-sm text-muted-foreground font-gc-reg">Score Distribution</div>
                  <div className="mt-2">
                    {testRunResults?.request_scores?.length > 0 ? (
                      <>
                        <div
                          style={{
                            position: "relative",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "24px",
                            margin: "0 auto",
                          }}>
                          {(() => {
                            const totalScores =
                              testRunResults.request_scores.reduce(
                                (sum, item) => sum + item.count,
                                0
                              );
                            const neutralScore =
                              testRunResults.request_scores.find(
                                (item) => item.score === 3
                              );

                            // Calculate the actual neutral portion, but cap it at 5%
                            const actualNeutralPortion = neutralScore
                              ? neutralScore.count / totalScores
                              : 0;
                            const neutralWidth = Math.min(
                              actualNeutralPortion * 100,
                              5
                            );

                            // Calculate non-neutral total (excluding score 3)
                            const nonNeutralScores =
                              testRunResults.request_scores.filter(
                                (item) => item.score !== 3
                              );
                            const nonNeutralTotal = nonNeutralScores.reduce(
                              (sum, item) => sum + item.count,
                              0
                            );

                            // Each side gets 47.5% of the total width (since center takes 5%)
                            const sideWidth = (100 - neutralWidth) / 2;

                            return (
                              <div
                                style={{
                                  position: "relative",
                                  width: "100%",
                                  height: "100%",
                                }}>
                                {/* Left side container */}
                                <div
                                  style={{
                                    position: "absolute",
                                    right: `${50 + neutralWidth / 2}%`,
                                    height: "100%",
                                    width: `${sideWidth}%`,
                                    display: "flex",
                                    justifyContent: "flex-end",
                                  }}>
                                  {[
                                    {
                                      score: 1,
                                      label: "POOR (-2)",
                                      color: "#f5222d",
                                    },
                                    {
                                      score: 2,
                                      label: "FAIR (-1)",
                                      color: "#ffa39e",
                                    },
                                  ].map(({ score, label, color }) => {
                                    const scoreData =
                                      testRunResults.request_scores.find(
                                        (item) => item.score === score
                                      );
                                    const tooltipPercentage = scoreData
                                      ? (scoreData.count / totalScores) * 100
                                      : 0;
                                    // Calculate width as proportion of non-neutral scores * available side width
                                    const widthPercentage =
                                      scoreData && nonNeutralTotal > 0
                                        ? (scoreData.count / nonNeutralTotal) *
                                          100
                                        : 0;

                                    return widthPercentage > 0 ? (
                                      <Tooltip
                                        key={label}
                                        title={`${label}: ${Math.round(
                                          tooltipPercentage
                                        )}%`}>
                                        <div
                                          onClick={() =>
                                            handleScoreClick(score)
                                          }
                                          style={{
                                            width: `${widthPercentage}%`,
                                            backgroundColor: color,
                                            height: "100%",
                                            transition:
                                              "width 0.3s ease, box-shadow 0.3s ease",
                                            cursor: "pointer",
                                            opacity:
                                              selectedScores.length === 0 ||
                                              selectedScores.includes(score)
                                                ? 1
                                                : 0.5,
                                            boxShadow: selectedScores.includes(
                                              score
                                            )
                                              ? "0 0 0 2px #1890ff"
                                              : "none",
                                            position: "relative",
                                            zIndex: selectedScores.includes(
                                              score
                                            )
                                              ? 0
                                              : "auto",
                                          }}
                                        />
                                      </Tooltip>
                                    ) : null;
                                  })}
                                </div>

                                {/* Center container for GOOD scores */}
                                <div
                                  style={{
                                    position: "absolute",
                                    left: `${50 - neutralWidth / 2}%`,
                                    width: `${neutralWidth}%`,
                                    height: "100%",
                                    backgroundColor: "#d9d9d9",
                                    transition: "all 0.3s ease",
                                  }}>
                                  {neutralScore && neutralWidth > 0 && (
                                    <Tooltip
                                      title={`GOOD (0): ${Math.round(
                                        actualNeutralPortion * 100
                                      )}%`}>
                                      <div
                                        onClick={() => handleScoreClick(3)}
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          cursor: "pointer",
                                          opacity:
                                            selectedScores.length === 0 ||
                                            selectedScores.includes(3)
                                              ? 1
                                              : 0.5,
                                          boxShadow: selectedScores.includes(3)
                                            ? "0 0 0 2px #1890ff"
                                            : "none",
                                          position: "relative",
                                          zIndex: selectedScores.includes(3)
                                            ? 0
                                            : "auto",
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                </div>

                                {/* Right side container */}
                                <div
                                  style={{
                                    position: "absolute",
                                    left: `${50 + neutralWidth / 2}%`,
                                    height: "100%",
                                    width: `${sideWidth}%`,
                                    display: "flex",
                                  }}>
                                  {[
                                    {
                                      score: 4,
                                      label: "GREAT (+1)",
                                      color: "#b7eb8f",
                                    },
                                    {
                                      score: 5,
                                      label: "PERFECT (+2)",
                                      color: "#52c41a",
                                    },
                                  ].map(({ score, label, color }) => {
                                    const scoreData =
                                      testRunResults.request_scores.find(
                                        (item) => item.score === score
                                      );
                                    const tooltipPercentage = scoreData
                                      ? (scoreData.count / totalScores) * 100
                                      : 0;
                                    // Calculate width as proportion of non-neutral scores * available side width
                                    const widthPercentage =
                                      scoreData && nonNeutralTotal > 0
                                        ? (scoreData.count / nonNeutralTotal) *
                                          100
                                        : 0;

                                    return widthPercentage > 0 ? (
                                      <Tooltip
                                        key={label}
                                        title={`${label}: ${Math.round(
                                          tooltipPercentage
                                        )}%`}>
                                        <div
                                          onClick={() =>
                                            handleScoreClick(score)
                                          }
                                          style={{
                                            width: `${widthPercentage}%`,
                                            backgroundColor: color,
                                            height: "100%",
                                            transition:
                                              "width 0.3s ease, box-shadow 0.3s ease",
                                            cursor: "pointer",
                                            opacity:
                                              selectedScores.length === 0 ||
                                              selectedScores.includes(score)
                                                ? 1
                                                : 0.5,
                                            boxShadow: selectedScores.includes(
                                              score
                                            )
                                              ? "0 0 0 2px #1890ff"
                                              : "none",
                                            position: "relative",
                                            zIndex: selectedScores.includes(
                                              score
                                            )
                                              ? 0
                                              : "auto",
                                          }}
                                        />
                                      </Tooltip>
                                    ) : null;
                                  })}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                        <div className="relative flex justify-between mt-2 text-xs text-muted-foreground">
                          <span>Poor</span>
                          <div className="absolute left-1/2 transform -translate-x-1/2">
                            Neutral
                          </div>
                          <span>Perfect</span>
                        </div>
                      </>
                    ) : (
                      <span className="text-sm text-muted-foreground font-gc-reg">No data available</span>
                    )}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Requests Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            <h2 className="text-xl font-gc-med text-foreground mb-6">Requests</h2>
            <TestRunRequestsTable
              requests={requests}
              loading={isRequestsLoading}
              apiKey={apiKey}
              currentPage={currentPage}
              totalPages={Math.ceil(totalRequests / 10)}
              totalItems={totalRequests}
              itemsPerPage={10}
              onPageChange={setCurrentPage}
              onRowClick={handleRowClick}
              onCompareClick={handleCompareClick}
              testSetId={testSetId}
              systemPrompt={testRun?.config?.system_prompt}
              selectedScores={selectedScores}
              onScoreFilterClear={handleScoreFilterClear}
            />
          </CardContent>
        </Card>
        {/* Configuration Modal */}
        <Modal
          title="Test Run Configuration"
          open={isConfigModalVisible}
          onCancel={() => setIsConfigModalVisible(false)}
          footer={null}
          width={800}>
          <ConfigurationPanel
            intentGroupId={intentGroupId ? Number(intentGroupId) : null}
            config={testRun?.config}
            updateConfig={() => {}}
            resetConfig={() => {}}
            isModalOpen={true}
            readOnly={true}
            allowSecondaryModel={false}
            showSystemPrompt={true}
          />
        </Modal>

        {/* Add the comparison modal */}
        {selectedComparisonRequestId && (
          <TestRunRequestComparison
            testRunId={Number(testRunId)}
            testRunRequestId={selectedComparisonRequestId}
            isVisible={isComparisonModalVisible}
            onClose={() => {
              setIsComparisonModalVisible(false);
              setSelectedComparisonRequestId(null);
            }}
            apiKey={apiKey}
            testSetId={testSetId}
            systemPrompt={testRun?.config?.system_prompt}
            intentGroupId={intentGroupId}
          />
        )}

        {/* Replace the old modal with the new component */}
        {selectedRequestId && (
          <RequestRepresentationModal
            requestId={selectedRequestId}
            isVisible={isRepresentationModalVisible}
            onClose={() => {
              setIsRepresentationModalVisible(false);
              setSelectedRequestId(null);
            }}
            apiKey={apiKey}
          />
        )}
      </div>
    </div>
  );
};

export default TestRunDetail;
